import { addDays, addMonths } from 'date-fns';
import { Booking, Resource } from '@/types';

export interface GenerateRecurrenceDatesOptions {
  /** Resources used to resolve the booking's resource for non-working-day filtering. */
  resources?: Resource[];
  /** Predicate deciding whether a generated date is a non-working day for the resource. */
  isNonWorkingDay?: (resource: Resource | undefined, date: Date) => boolean;
}

/**
 * Generates the recurrence occurrence dates that follow a booking's own slot.
 *
 * The booking itself is the series anchor, so it is never returned. Resource
 * awareness is injected via options so this stays a pure function.
 */
export function generateRecurrenceDates(
  booking: Partial<Booking>,
  originalBooking: Booking,
  options: GenerateRecurrenceDatesOptions = {}
): Date[] {
  const recurrence = booking.recurrence || originalBooking.recurrence;
  if (!recurrence || recurrence.pattern === 'none') {
    return [];
  }

  const bookingStartDate = booking.startTime ? new Date(booking.startTime) : new Date(originalBooking.startTime);
  const interval = recurrence.interval || 1;
  const dates: Date[] = [];
  const seriesStartDate = recurrence.startDate ? new Date(recurrence.startDate) : undefined;
  const seriesEndDate = recurrence.endDate ? new Date(recurrence.endDate) : undefined;

  // A series that ends on a date runs until that date: `occurrences` only caps the
  // count for "end after N", and a series with neither falls back to 52 so it can
  // never run away. `withinLimit` enforces this; the loops below only need a scan
  // bound big enough to reach the end date (daily for five years).
  const occurrenceLimit = seriesEndDate && recurrence.endType !== 'after'
    ? undefined
    : (recurrence.occurrences || 52);
  const maxScanSteps = occurrenceLimit ?? 1830;

  // Resolve the resource for non-working day filtering
  const resourceId = booking.resourceId || booking.resourceIds?.[0] || originalBooking.resourceId || originalBooking.resourceIds?.[0];
  const resource = resourceId
    ? (options.resources ?? []).find(r => String(r.id) === String(resourceId))
    : undefined;
  const isNonWorkingDay = options.isNonWorkingDay;
  const filterNonWorkingDays = (d: Date[]) =>
    isNonWorkingDay ? d.filter(date => !isNonWorkingDay(resource, date)) : d;

  // Safety: if endDate is in the past or before startDate, use a sensible default
  if (recurrence.endType === 'on-date' && seriesEndDate && seriesEndDate <= bookingStartDate) {
    const fallbackEnd = addMonths(bookingStartDate, 3);
    for (let i = 1; i < maxScanSteps; i++) {
      let nextDate: Date;
      switch (recurrence.pattern) {
        case 'daily': nextDate = addDays(bookingStartDate, i * interval); break;
        case 'weekly': nextDate = addDays(bookingStartDate, i * 7 * interval); break;
        case 'monthly': nextDate = addMonths(bookingStartDate, i * interval); break;
        default: return dates;
      }
      if (nextDate > fallbackEnd) break;
      dates.push(nextDate);
    }
    return filterNonWorkingDays(dates);
  }

  const withinLimit = (d: Date, count: number): boolean => {
    if (seriesEndDate) {
      const dNorm = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const endNorm = new Date(seriesEndDate.getFullYear(), seriesEndDate.getMonth(), seriesEndDate.getDate());
      if (dNorm > endNorm) return false;
    }
    if (occurrenceLimit !== undefined && count >= occurrenceLimit - 1) return false;
    return true;
  };

  // The booking's own slot is the anchor, so only later dates are generated. Monthly
  // patterns start scanning in the booking's own month (i = 0) because the configured
  // day can still be ahead of the booking — e.g. a booking on the 5th repeating on the
  // 23rd owes an occurrence in that same month.
  const afterBookingStart = (d: Date): boolean => {
    const dNorm = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const bNorm = new Date(bookingStartDate.getFullYear(), bookingStartDate.getMonth(), bookingStartDate.getDate());
    return dNorm > bNorm;
  };

  // Filter helper: only include dates that fall on or after the series start
  const afterSeriesStart = (d: Date): boolean => {
    if (!seriesStartDate) return true;
    const dNorm = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const sNorm = new Date(seriesStartDate.getFullYear(), seriesStartDate.getMonth(), seriesStartDate.getDate());
    return dNorm >= sNorm;
  };

  if (recurrence.pattern === 'weekly' && recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
    // Weekly with specific days of week selected
    const dayNameToIndex: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6,
    };
    const targetDayIndices = recurrence.daysOfWeek
      .map(d => dayNameToIndex[d.toLowerCase()])
      .filter(i => i !== undefined)
      .sort((a, b) => a - b);

    if (targetDayIndices.length === 0) return dates;

    // Start scanning from the day after the booking start date
    let currentDate = addDays(bookingStartDate, 1);
    let weekCounter = 0;
    let lastWeekNumber = -1;

    while (dates.length < maxScanSteps) {
      const currentDayOfWeek = currentDate.getDay();
      // Track week boundaries for interval > 1
      const weeksSinceStart = Math.floor(
        (currentDate.getTime() - bookingStartDate.getTime()) / (7 * 86400000)
      );

      if (weeksSinceStart !== lastWeekNumber) {
        lastWeekNumber = weeksSinceStart;
        weekCounter = weeksSinceStart;
      }

      // Only include dates from weeks that match the interval
      if (weekCounter % interval === 0 && targetDayIndices.includes(currentDayOfWeek)) {
        if (!withinLimit(currentDate, dates.length)) break;
        if (afterSeriesStart(currentDate)) {
          dates.push(new Date(currentDate));
        }
      }

      // If we're past the end date, stop scanning
      if (seriesEndDate) {
        const cNorm = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const endNorm = new Date(seriesEndDate.getFullYear(), seriesEndDate.getMonth(), seriesEndDate.getDate());
        if (cNorm > endNorm) break;
      }

      currentDate = addDays(currentDate, 1);
      // Safety: don't scan more than 5 years
      if (currentDate.getTime() - bookingStartDate.getTime() > 5 * 365 * 86400000) break;
    }
  } else if (recurrence.pattern === 'monthly') {
    // Monthly recurrence
    if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
      // Option 2: "Monthly on the Xth Weekday"
      const { occurrence, weekday } = recurrence.relativeWeekday;
      const dayNameToIndex: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6,
      };
      const targetDayIndex = dayNameToIndex[weekday.toLowerCase()] ?? 0;
      const occurrenceMap: Record<string, number> = {
        first: 1, second: 2, third: 3, fourth: 4, last: -1,
      };
      const targetOccurrence = occurrenceMap[occurrence] ?? 1;

      for (let i = 0; i < maxScanSteps + 1; i++) {
        const targetMonth = new Date(bookingStartDate);
        targetMonth.setDate(1);
        targetMonth.setMonth(targetMonth.getMonth() + i * interval);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth();

        let foundDate: Date | null = null;
        if (targetOccurrence === -1) {
          // Last occurrence: scan backwards from end of month
          const lastDay = new Date(year, month + 1, 0).getDate();
          for (let d = lastDay; d >= 1; d--) {
            const candidate = new Date(year, month, d);
            if (candidate.getDay() === targetDayIndex) {
              foundDate = candidate;
              break;
            }
          }
        } else {
          // Nth occurrence: scan forwards
          let count = 0;
          for (let d = 1; d <= 31; d++) {
            const candidate = new Date(year, month, d);
            if (candidate.getMonth() !== month) break;
            if (candidate.getDay() === targetDayIndex) {
              count++;
              if (count === targetOccurrence) {
                foundDate = candidate;
                break;
              }
            }
          }
        }

        if (foundDate && afterBookingStart(foundDate)) {
          if (!withinLimit(foundDate, dates.length)) break;
          if (afterSeriesStart(foundDate)) {
            dates.push(foundDate);
          }
        }
      }
    } else {
      // Option 1: "Monthly on day N" (default)
      const targetDay = recurrence.dayOfMonth ?? bookingStartDate.getDate();
      for (let i = 0; i < maxScanSteps + 1; i++) {
        const targetMonth = new Date(bookingStartDate);
        // Land on the first of the month before shifting, so a booking on the 31st
        // cannot roll the month over (Jan 31 + 1 month would otherwise be Mar 3).
        targetMonth.setDate(1);
        targetMonth.setMonth(targetMonth.getMonth() + i * interval);
        // Clamp to last day of month if target day exceeds month length
        const lastDayOfMonth = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 1,
          0
        ).getDate();
        targetMonth.setDate(Math.min(targetDay, lastDayOfMonth));

        if (!afterBookingStart(targetMonth)) continue;
        if (!withinLimit(targetMonth, dates.length)) break;
        if (afterSeriesStart(targetMonth)) {
          dates.push(targetMonth);
        }
      }
    }
  } else {
    // Daily or simple weekly (no specific days)
    for (let i = 1; i < maxScanSteps; i++) {
      let nextDate: Date;
      switch (recurrence.pattern) {
        case 'daily':
          nextDate = addDays(bookingStartDate, i * interval);
          break;
        case 'weekly':
          nextDate = addDays(bookingStartDate, i * 7 * interval);
          break;
        default:
          return filterNonWorkingDays(dates);
      }

      if (!withinLimit(nextDate, dates.length)) break;
      if (afterSeriesStart(nextDate)) {
        dates.push(nextDate);
      }
    }
  }

  return filterNonWorkingDays(dates);
}
